import { useState, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CheckCircle, Clipboard, Copy } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

// Form validation schema
const formSchema = z.object({
  name: z
    .string()
    .min(2, { message: "Name must be at least 2 characters" })
    .max(100, { message: "Name can't be longer than 100 characters" }),
  email: z
    .string()
    .email({ message: "Please enter a valid email address" }),
  phone: z
    .string()
    .min(5, { message: "Please enter a valid phone number" })
    .max(20, { message: "Phone number can't be longer than 20 characters" })
    .optional(),
  qualifications: z
    .string()
    .min(10, { message: "Please tell us why you think you're qualified to refer licensing deals (min 10 characters)" })
    .max(500, { message: "Please keep your explanation under 500 characters" }),
});

type FormValues = z.infer<typeof formSchema>;

export default function Referral() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setLocation] = useLocation();
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  // Automatically scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const defaultValues: Partial<FormValues> = {
    name: "",
    email: "",
    phone: "",
    qualifications: "",
  };

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const generateReferralCode = (name: string, email: string) => {
    // Create a simple referral code based on name and a random number
    const namePart = name.trim().toLowerCase().replace(/\s+/g, "-").substring(0, 10);
    const randomPart = Math.floor(10000 + Math.random() * 90000);
    return `NLN-${namePart}-${randomPart}`;
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(referralCode);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
    
    toast({
      title: "Copied to clipboard",
      description: "Your referral code has been copied to clipboard.",
    });
  };

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    try {
      // Generate a unique referral code for this user
      const code = generateReferralCode(data.name, data.email);
      setReferralCode(code);
      
      const submitData = {
        ...data,
        referralCode: code,
      };
      
      const response = await apiRequest("POST", "/api/referral-applications", submitData);
      const result = await response.json();
      
      if (result.success) {
        // Show the modal with the referral code
        setShowCodeModal(true);
      } else {
        throw new Error(result.message || "Submission failed");
      }
    } catch (error: any) {
      toast({
        title: "Submission Failed",
        description: error.message || "There was an error submitting your application. Please try again.",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  const handleAcceptTerms = () => {
    setShowCodeModal(false);
    
    toast({
      title: "Referral Program Joined",
      description: "Welcome to the NoLicense Nearby referral program! Your code has been emailed to you along with helpful resources.",
    });
    
    // Clear form and redirect to home page
    form.reset();
    setIsSubmitting(false);
    setLocation("/");
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold mb-4">Join Our Referral Program</h1>
            <p className="text-muted-foreground">
              Earn commission by referring clients who need music licensing for their projects.
              This isn't a job application - it's an opportunity to make extra money through referrals.
            </p>
          </div>

          <div className="bg-card border border-border p-8 rounded-xl shadow-sm">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Your full name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input 
                          type="email" 
                          placeholder="your.email@example.com" 
                          {...field} 
                        />
                      </FormControl>
                      <FormDescription>
                        We'll use this email to send you referral information and commission statements.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number (Optional)</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Your phone number" 
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="qualifications"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Why are you qualified to refer music licensing deals?</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Tell us about your connections or experience that would help you refer clients who need music licensing." 
                          className="min-h-[120px]"
                          {...field} 
                        />
                      </FormControl>
                      <FormDescription>
                        This helps us understand how you plan to refer clients to NoLicense Nearby.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6">
                  <h3 className="text-sm font-medium mb-2">Important Information:</h3>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    <li>• This is not a job application, but an opportunity to earn commissions through referrals</li>
                    <li>• Commissions are paid only when referred clients complete a music licensing purchase</li>
                    <li>• Commission rate is 10% based on the license value</li>
                    <li>• Commission statements and payments are processed monthly</li>
                    <li>• You'll receive a unique referral code to share with potential clients</li>
                  </ul>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-primary hover:bg-primary/90 text-black dark:text-white" 
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Application"
                  )}
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </div>

      {/* Referral Code Modal */}
      <Dialog open={showCodeModal} onOpenChange={setShowCodeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <CheckCircle className="h-5 w-5 text-primary mr-2" />
              Your Referral Application is Approved!
            </DialogTitle>
            <DialogDescription>
              You're now part of the NoLicense Nearby referral program. Here's your unique referral code.
            </DialogDescription>
          </DialogHeader>
          
          <div className="p-4 bg-muted rounded-lg flex items-center justify-between">
            <code className="font-mono text-lg font-semibold">{referralCode}</code>
            <Button size="sm" variant="outline" onClick={copyToClipboard}>
              {copiedCode ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              <strong>Please save this code!</strong> Share it with anyone looking to license music.
              When they enter it during checkout, you'll earn a 10% commission on their purchase.
            </p>
            <p>
              We'll send an onboarding email to <strong>{form.getValues().email}</strong> with your referral code, helpful tips,
              and resources to help you find licensing deals.
            </p>
          </div>
          
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 mb-3">
            <h4 className="text-sm font-medium mb-1">Referral Program Terms:</h4>
            <ul className="text-xs space-y-1 text-muted-foreground">
              <li>• This is not a job application, but an opportunity to earn commissions through referrals</li>
              <li>• Commissions are paid only when referred clients complete a music licensing purchase</li>
              <li>• Commission rate is 10% based on the license value</li>
              <li>• Commission statements and payments are processed monthly</li>
            </ul>
          </div>
          
          <div className="flex items-start space-x-2">
            <Checkbox 
              id="terms" 
              checked={termsAccepted} 
              onCheckedChange={(checked) => setTermsAccepted(checked as boolean)} 
            />
            <label 
              htmlFor="terms" 
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              I have recorded my referral code and agree to the Referral Program Terms as shown above. I understand that 
              NoLicense Nearby reserves the right to modify or terminate the referral program at any time.
            </label>
          </div>
          
          <DialogFooter>
            <Button 
              type="button" 
              className="bg-primary text-black dark:text-white" 
              onClick={handleAcceptTerms}
              disabled={!termsAccepted}
            >
              Accept & Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}